import {useCallback, useMemo, useState} from 'react';

const normalize = (text: unknown) => String(text ?? '').trim().toLowerCase();

// Splits "Ad Tag Requests", "connection_name" and "connectionServerReceived" into words.
const words = (text: unknown) => String(text ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * Lower is more relevant: exact label, exact value, label starts with the query,
 * a word in the label starts with it, then any other match the list's own filter kept.
 */
export function searchRank(label: unknown, value: unknown, query: string): number {
    const q = normalize(query);
    if (!q) return 0;
    const l = normalize(label);
    if (l === q) return 0;
    if (normalize(value) === q) return 1;
    if (l.startsWith(q)) return 2;
    const firstWord = words(q)[0];
    if (firstWord && words(label).some((word) => word.startsWith(firstWord))) return 3;
    return 4;
}

/** Orders items by relevance to the query; equally relevant items keep their original order. */
export function rankBySearch<T>(items: T[] = [], query: string, labelKey = 'label', valueKey = 'value'): T[] {
    if (!normalize(query)) return items;
    return items
        .map((item, index) => ({item, index, rank: searchRank(item?.[labelKey], item?.[valueKey], query)}))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(({item}) => item);
}

/** For searchable ui-kit selects, which filter `items` but keep their order. */
export function useSearchRankedItems<T>(items: T[], labelKey = 'label', valueKey = 'value') {
    const [query, setQuery] = useState('');
    const ranked = useMemo(() => rankBySearch(items, query, labelKey, valueKey), [items, query, labelKey, valueKey]);
    const onSearch = useCallback((value: string) => setQuery(value || ''), []);
    return {items: ranked, onSearch};
}
